import {Injectable, isDevMode} from '@angular/core';
import {GruppeId, Språk} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {BrukerkontekstTilbyder} from "../BrukerkontekstTilbyder";
import {merge, Observable, of} from "rxjs";
import {map} from "rxjs/operators";
import {Debugger} from "debug";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";

const debug: Debugger = require('debug')('GjeldendeLokale');


/**
 * Tjeneste som gir tilgang til gjeldende språk og region.
 *
 * Bruker kan endre språk via ajs komponenten Språkvalg.
 * Endring i språk generer eventen
 * @todo sørg for at endring i valgt språk via ajs-komp for dette propagerer hit
 */
@Injectable()
export class GjeldendeLokale {

    private _brukersLokaleSomStrøm:Observable<Lokaleinformasjon>;

    constructor(private readonly kontekstTilbyder: BrukerkontekstTilbyder) {
        this._brukersLokaleSomStrøm = merge(
          of(this.standardLokale()), //gir en initiell verdi
          this.kontekstTilbyder.autentisertBrukerSomStrøm()
            .pipe(map(kontekst => Lokaleinformasjon.fraJavaLocale(kontekst.bruker.spraak))))
    }

    brukersLokale(): Lokaleinformasjon {
        if (this.kontekstTilbyder.erIkkeAutentisert()) return this.standardLokale();
        return Lokaleinformasjon.fraJavaLocale(this.kontekstTilbyder.autentisertBruker().bruker.spraak);
    }

    /** Leverer brukers lokale som en observable. Leverer umiddelbart nåværende lokale, lever så ny verdi hver gang brukers lokale endres. */
    brukersLokaleSomStrøm(): Observable<Lokaleinformasjon> {
        return this._brukersLokaleSomStrøm;
    }

    gruppensLokale(id: GruppeId): Lokaleinformasjon {
        if (this.kontekstTilbyder.erIkkeAutentisert()) return this.standardLokale();
        return Lokaleinformasjon.fraJavaLocale(this.kontekstTilbyder.autentisertBruker().finnAutentiserteGruppe(id)!.oppsett.spraak);
    }

    //hjelpemetoder

    /** Returnerer lokale basert på nettleseroppsett e.l.
     * @todo les fra ajs Språklager */
    private standardLokale(){
        //en siste utvei:
        return Lokaleinformasjon.standardLokale;
    }
}


export type Region = 'NO' | 'GB' | '';

export class Lokaleinformasjon {
     static standardLokale = Lokaleinformasjon.fraJavaLocale(Språk.norsk_bokmål);

    /** Oppretter Språkinformasjon fra språkrepresentasjon vi opererer med på java-siden.
     * Se enumen no.systemfabrikken.lederplan.tjenester.i18n.I18nStøttedeSpråk.Språk .
     * Mulige verdier er per nå 'nb, 'nn', 'en'.
     * MERK at denne representasjonen mangler informasjon om språk (language), informasjon om region mangler,
     * dermed representeres ikke feks skillet mellom britisk og amerikansk */
    static fraJavaLocale(javaLocale: Språk): Lokaleinformasjon {
        return new Lokaleinformasjon(javaLocale, javaLocale, regionFraJavaLocale(javaLocale));
    }

    constructor(private lokalestreng: string, public readonly språk: Språk, public readonly region: Region) {
    }

    /** strengrepresentasjon enkodet som ..?
     * @todo format ?*/
    somLocaleId(): string {
        return this.lokalestreng;
    }

    /** Returnerer locale sensitiv formatstreng for dato */
    datoformat(){
        switch (this.lokalestreng){
            case Språk.norsk_bokmål:
            case Språk.norsk_nynorsk: return 'dd.MM.yyyy';
            case Språk.engelsk: return 'MM/dd/yyyy';
            default:
                if (isDevMode())
                    throw new Error(`Lokaleinformasjon.datoformat: ukjent locale '${this.lokalestreng}'`)
                debug(`Lokaleinformasjon.datoformat: ukjent locale '${this.lokalestreng}'`)
                return 'dd.MM.yyyy';
        }
    }

    somBrukerrettetStreng(oversettelser:Oversettelser):string {
        switch (this.lokalestreng) {
            case Språk.norsk_bokmål:
                return oversettelser.oversett(i18n('Bokmål'));
            case Språk.norsk_nynorsk:
                return oversettelser.oversett(i18n('Nynorsk'));
            case Språk.engelsk:
                return oversettelser.oversett(i18n('Engelsk'));
            default:
                return this.lokalestreng; // bør aldri skje
        }
    }
}

/** Returnerer region/land utledet av lokalestreng.
 * På sikt bør region ligge eksplisitt i javaLocale */
function regionFraJavaLocale(javaLocale: Språk): Region {
    switch (javaLocale){
        case Språk.norsk_bokmål: return 'NO';
        case Språk.norsk_nynorsk: return 'NO';
        case Språk.engelsk: return 'GB';
        default:
            if (isDevMode())
                console.warn(`regionFraJavaLocale: ukjent javaLocale '${javaLocale}'`)
            return '';
    }
}
